(() => {
  // ---- CONFIG
  const SLIDER_SEL = '.card-image-slider';
  const LIST_SEL   = '.card-image-slider__list';
  const FILL_SEL   = '.card-image-slider__progress-fill';
  const BAR_SEL    = '.card-image-slider__progress';
  const PREV_SEL   = '.card-image-slider__btn--prev';
  const NEXT_SEL   = '.card-image-slider__btn--next';
  const MAX_STEPS  = 4; // progress segments cap

  // ---- INTERNAL STATE
  const instances = new Map(); // sliderEl -> instance
  let ticking = false;

  // ---- UTIL
  const scheduleTick = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      instances.forEach(inst => {
        if (!inst.dirty) return;
        inst.dirty = false;
        flushUI(inst);
      });
      // If anything marked dirty during flush, schedule again
      if ([...instances.values()].some(i => i.dirty)) scheduleTick();
    });
  };

  const setBtnState = (btn, disabled) => {
    if (!btn) return;
    btn.disabled = !!disabled;
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    btn.classList.toggle('card-image-slider__btn--disabled', !!disabled);
  };

  // ---- OBSERVERS (shared)
  const io = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const slide = entry.target;
      const slider = slide.__cardSliderParent;
      const inst = instances.get(slider);
      if (!inst) continue;
      const idx = Number(slide.getAttribute('data-step-index')) || 0;
      if (idx !== inst.index) {
        inst.index = idx;
        inst.dirty = true;
      }
    }
    scheduleTick();
  }, { threshold: [0.5] });

  // ---- UI
  function flushUI(inst) {
    const { list, bar, fill, steps, maxIndex, index, segmentPct } = inst;
    const stepPos = maxIndex === 0 ? 0 : Math.round((index / maxIndex) * (steps - 1));
    fill.style.width = `${segmentPct}%`;
    fill.style.transform = `translateX(${stepPos * 100}%)`;
    bar.style.display = steps > 1 ? '' : 'none';
    setBtnState(inst.prevBtn, index <= 0);
    setBtnState(inst.nextBtn, index >= maxIndex);
  }

  function bind(slider) {
    if (instances.has(slider)) return;

    const list = slider.querySelector(LIST_SEL);
    const fill = slider.querySelector(FILL_SEL);
    const bar  = slider.querySelector(BAR_SEL);
    if (!list || !fill || !bar) return;

    const slides = Array.from(list.querySelectorAll('.slider__slide'));
    const steps = Math.max(1, Math.min(MAX_STEPS, slides.length));
    const maxIndex = Math.max(0, slides.length - 1);
    const segmentPct = 100 / steps;

    // Annotate + observe slides
    slides.forEach((s, i) => {
      s.setAttribute('data-step-index', i);
      s.__cardSliderParent = slider;
      io.observe(s);
    });

    const prevBtn = slider.querySelector(PREV_SEL);
    const nextBtn = slider.querySelector(NEXT_SEL);

    const inst = {
      slider, list, bar, fill, slides,
      prevBtn, nextBtn,
      steps, maxIndex, segmentPct,
      index: 0,
      dirty: true
    };
    instances.set(slider, inst);

    // Controls
    const scrollByStep = (dir) => {
      const w = list.clientWidth || 0;
      if (!w) return;
      const target = Math.max(0, Math.min(maxIndex, inst.index + dir));
      if (target === inst.index) return;
      inst.index = target;
      inst.dirty = true;
      scheduleTick();
      list.scrollTo({ left: target * w, behavior: 'smooth' });
    };

    prevBtn && prevBtn.addEventListener('click', e => { e.preventDefault(); scrollByStep(-1); });
    nextBtn && nextBtn.addEventListener('click', e => { e.preventDefault(); scrollByStep(1); });

    // Scroll sync (throttled to 1/frame)
    let scrollRAF = 0;
    list.addEventListener('scroll', () => {
      if (scrollRAF) return;
      scrollRAF = requestAnimationFrame(() => {
        scrollRAF = 0;
        const w = list.clientWidth || 1;
        const idx = Math.round(list.scrollLeft / w);
        if (idx !== inst.index) {
          inst.index = Math.max(0, Math.min(maxIndex, idx));
          inst.dirty = true;
          scheduleTick();
        }
      });
    }, { passive: true });

    // Resize sync
    let resizeRAF = 0;
    const onResize = () => {
      if (resizeRAF) return;
      resizeRAF = requestAnimationFrame(() => {
        resizeRAF = 0;
        inst.dirty = true;
        scheduleTick();
      });
    };
    window.addEventListener('resize', onResize);
    inst.onResize = onResize; // stored so the MutationObserver cleanup can remove it

    // Images loading can shift layout
    slides.forEach(slide => {
      const img = slide.querySelector('img');
      if (img && !img.complete) img.addEventListener('load', () => {
        inst.dirty = true;
        scheduleTick();
      }, { once: true });
    });

    // Initialize
    list.scrollLeft = 0;
    slider.setAttribute('data-progress-bound', 'true');
    scheduleTick();
  }

  function initAll(root = document) {
    root.querySelectorAll(`${SLIDER_SEL}:not([data-progress-bound])`).forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());

  // Re-init on section loads
  document.addEventListener('shopify:section:load', e => initAll(e.target || document));

  // Watch only likely containers (avoid whole document for perf)
  const moRoot = document.querySelector('#main-content') || document.body;
  const mo = new MutationObserver(muts => {
    let needsInit = false;
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches(SLIDER_SEL)) { bind(n); }
        else if (n.querySelector && n.querySelector(SLIDER_SEL)) { needsInit = true; }
      }
      // Cleanup removed sliders to avoid leaks
      for (const n of m.removedNodes) {
        if (n.nodeType !== 1) continue;
        const gone = n.matches?.(SLIDER_SEL) ? [n] : Array.from(n.querySelectorAll?.(SLIDER_SEL) || []);
        gone.forEach(sl => {
          const inst = instances.get(sl);
          if (!inst) return;
          // Release the external references that otherwise keep the detached
          // slider (and its whole subtree) alive across AJAX filter swaps: the
          // per-slider window resize listener and the shared IntersectionObserver's
          // slide observations. Internal listeners (scroll/click/img-load) are on
          // the subtree itself and get GC'd once these external refs are gone.
          if (inst.onResize) window.removeEventListener('resize', inst.onResize);
          if (inst.slides) inst.slides.forEach(s => { io.unobserve(s); delete s.__cardSliderParent; });
          instances.delete(sl);
        });
      }
    }
    if (needsInit) initAll(moRoot);
  });
  mo.observe(moRoot, { childList: true, subtree: true });

  // Optional: pause work when tab hidden (saves battery)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleTick();
  });
})();